import {
	Listbox,
	ListboxButton,
	ListboxOption,
	ListboxOptions,
	type ListboxOptionsProps,
	Transition,
} from "@headlessui/react";
import { Option } from "@inrev/core";
import { Fragment, type MouseEvent, useId, useMemo } from "react";
import { useLabelledBy } from "../../lib/useLabelledBy";
import { cn } from "../../lib/utils";
import { CheckboxGlyph, Icon, Text } from "../../primitives";
import { PortaledTooltip } from "../../ui/Tooltip";
import { FittedLabels } from "./FittedLabels";
import { inputSize } from "./inputSize";

export type MultiSelectOption = {
	label: string;
	value: string;
	tooltip?: string;
	className?: string;
};

export type MultiSelectDropdownInputProps = {
	value: string[] | undefined;
	onChange: (value: string[]) => void;
	options: MultiSelectOption[];
	/**
	 * Mutually exclusive "none of the above" option. Picking it clears every other selection,
	 * and picking anything else clears it — so the two can never be checked together.
	 */
	noneValue?: string;
	/** Label rendered in the button when `noneValue` is the selection. */
	noneLabel?: string;
	placeholder?: string;
	compact?: boolean;
	disabled?: boolean;
	readonly?: boolean;
	error?: boolean;
	buttonId?: string;
	"aria-labelledby"?: string;
	testId?: string;
	className?: string;
	buttonClassName?: string;
	optionsClassName?: string;
	/** Portals and floats the option list so overflow-hidden ancestors can't clip it. */
	anchor?: ListboxOptionsProps["anchor"];
};

const DEFAULT_ANCHOR = { to: "bottom end", gap: 4 } as const;

/**
 * The anchored option list is portaled and sits well above the app's normal stacking range, so the
 * tooltip has to clear it explicitly — `PortaledTooltip`'s default is only just above `Modal`.
 */
const OPTIONS_Z_INDEX = 200001;
const TOOLTIP_Z_INDEX = OPTIONS_Z_INDEX + 1;

const stopPropagation = (event: MouseEvent) => event.stopPropagation();

export const MultiSelectDropdownInput = ({
	value,
	onChange,
	options,
	noneValue,
	noneLabel = "None",
	placeholder = "Select...",
	compact,
	disabled,
	readonly,
	error = false,
	buttonId,
	"aria-labelledby": ariaLabelledBy,
	testId,
	className,
	buttonClassName,
	optionsClassName,
	anchor = DEFAULT_ANCHOR,
}: MultiSelectDropdownInputProps) => {
	const selected = useMemo(() => value ?? [], [value]);
	const isNoneSelected = noneValue !== undefined && selected.includes(noneValue);

	// One tooltip box per control, anchored by every option that has content — cheaper than a
	// `<ReactTooltip>` per row, and the id stays stable across renders.
	const generatedId = useId();
	const tooltipId = `multiselect-tooltip-${testId ?? generatedId}`;
	const hasTooltips = options.some((option) => Boolean(option.tooltip));

	const selectedLabels = useMemo(() => {
		const labelByValue = new Map(options.map((option) => [option.value, option.label] as const));
		return Option.filterNone(selected.map((entry) => labelByValue.get(entry)));
	}, [options, selected]);

	const handleChange = (next: string[]) => {
		if (noneValue === undefined) {
			onChange(next);
			return;
		}
		const addedNone = next.includes(noneValue) && !isNoneSelected;
		if (addedNone) {
			onChange([noneValue]);
			return;
		}
		onChange(next.filter((entry) => entry !== noneValue));
	};

	const buttonRef = useLabelledBy<HTMLButtonElement>(ariaLabelledBy);
	const { frame, paddingX, text } = inputSize(compact);

	return (
		<Listbox
			multiple
			value={selected}
			onChange={handleChange}
			disabled={options.length === 0 || disabled || readonly}
			as="div"
			className={cn("relative h-fit", className)}
		>
			<ListboxButton
				id={buttonId}
				ref={buttonRef}
				data-testid={testId ?? "multiselect-button"}
				type="button"
				className={cn(
					"group flex items-center w-full text-left space-x-[6px] text-primary cursor-pointer rounded-md bg-surface-light/70 outline outline-[1px] outline-offset-[-1px] outline-bc-strong focus-visible:outline-text-secondary focus-visible:outline-[2px] data-[headlessui-state=open]:outline-text-secondary data-[headlessui-state=open]:outline-[2px] disabled:cursor-default",
					frame,
					paddingX,
					disabled ? "cursor-not-allowed opacity-50" : "hover:outline-text-tertiary",
					readonly ? "bg-surface-neutral/70 opacity-100 outline-[0px]" : undefined,
					error &&
						"outline-danger hover:outline-danger focus-visible:outline-danger data-[headlessui-state=open]:outline-danger",
					buttonClassName,
				)}
			>
				{!isNoneSelected && selectedLabels.length > 0 ? (
					<FittedLabels labels={selectedLabels} />
				) : (
					<Text
						truncate
						color={isNoneSelected ? undefined : "secondary"}
						className="min-w-0 flex-1"
					>
						{isNoneSelected ? noneLabel : placeholder}
					</Text>
				)}
				<Icon type="chevron-down" size="mini" color="tertiary" />
			</ListboxButton>
			<Transition
				as={Fragment}
				leave="transition ease-in duration-75"
				leaveFrom="opacity-100"
				leaveTo="opacity-0"
			>
				<ListboxOptions
					anchor={anchor}
					data-testid={testId ? `${testId}-options` : undefined}
					style={anchor ? { zIndex: OPTIONS_Z_INDEX } : undefined}
					className={cn(
						"w-fit max-w-[360px] max-h-[300px] overflow-y-auto py-[4px] text-secondary font-normal rounded bg-surface-base shadow-lg cursor-pointer select-none outline outline-[1px] outline-offset-[-1px] outline-bc-default",
						text,
						anchor ? "min-w-[var(--button-width)]" : "absolute min-w-full mt-1 z-[5000]",
						optionsClassName,
					)}
				>
					{options.map((option) => {
						const checked = selected.includes(option.value);
						const dimmed = isNoneSelected && option.value !== noneValue;
						return (
							<ListboxOption
								data-testid="multiselect-option"
								key={option.value}
								value={option.value}
								className={cn(
									"flex items-start gap-[10px] w-full py-[7px] text-primary data-[focus]:bg-surface-neutral",
									paddingX,
									dimmed && "opacity-50",
									option.className,
								)}
							>
								<span className="mt-[2px]">
									<CheckboxGlyph
										size="sm"
										checked={checked}
										data-testid={`multiselect-checkbox-${option.value}`}
									/>
								</span>
								<span className="flex-1 flex items-start gap-[5px]">
									{option.label}
									{option.tooltip && (
										<span
											data-tooltip-html={option.tooltip}
											data-tooltip-id={tooltipId}
											data-testid={`multiselect-tooltip-${option.value}`}
											// The anchor sits inside the option, so a click on it would otherwise
											// toggle the selection it is only trying to explain.
											onClick={stopPropagation}
											className="mt-[2px] inline-flex shrink-0 cursor-pointer text-tertiary hover:text-secondary"
										>
											{/* Sized to match `ui/Tooltip`'s condensed icon so the option tooltips
											    and the field-label tooltips in the same card read as one affordance. */}
											<Icon
												type="information-circle"
												size="mini"
												className="fill-surface-light stroke-[1.75px]"
											/>
										</span>
									)}
								</span>
							</ListboxOption>
						);
					})}
				</ListboxOptions>
			</Transition>
			{/* Portaled: the option list scrolls, so an inline tooltip box gets clipped by its overflow. */}
			{hasTooltips && (
				<PortaledTooltip
					id={tooltipId}
					className="font-normal"
					style={{ zIndex: TOOLTIP_Z_INDEX }}
				/>
			)}
		</Listbox>
	);
};
